import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import BuilderRightSideBar from "../../Layouts/BuilderRightSideBar";
import DropLayout from "../../components/ReactGridLayout/DropArea/DropLayout";
import { useScreenContext } from "../../context/ScreenContext";
import defaultImage from "../../images/defaultImage.png";
import { getDefaultConfig } from "../../utils/Config/getDefaultConfig";
import { getDropDimensionForScreen } from "../../utils/GridLayoutFunc";
import EditScreenField from "./EditScreenField";
import { hasOwn } from "../../commonFunctions/arrayFunc";
import { generateObjectId } from "../../utils/objectIdUtils";
import { toastError } from "../../utils/toastUtils";

const inlineElements = [
  "button",
  "dropdown",
  "smview",
  "html",
  "image",
  "label",
  "spacer",
  "tabview",
  "form",
  "button-group",
  "dropdown-navigate"
];

const screenBlocks = ["recordsview", "chart", "detailsview", "pivotcharts"];

const ScreenGridLayout = ({ dragFromOutside, setDragFromOutside, allApplicationData }) => {
  const { selectedScreen, layoutData, layouts, setLayouts, currentScreenData } = useScreenContext();
  const navigate = useNavigate();
  const { appId } = useParams();
  const { nestedGridData } = useScreenContext();
  const [editElementRightsidebar, setEditElementRightsidebar] = useState({
    id: "",
    type: "",
    show: false,
    config: {}
  });
  const device = selectedScreen.parentData.split("/")[0];

  const getAppBlockData = (id) => {
    let finalBlock = allApplicationData[id];
    if (!hasOwn(finalBlock, "config")) {
      //right now every block has config but old block has no config so letter will remove this condition
      finalBlock.config = finalBlock.data;
      delete finalBlock["data"];
    }
    return finalBlock;
  };
  const getElementConfig = (element) => {
    const { type, id } = element;
    switch (type) {
      case "button":
      case "button-group":
      case "dropdown":
      case "dropdown-navigate":
        return getDefaultConfig(type);
      case "smview":
        return { config: { sm_id: "" } };
      case "html":
        return { content: "" };
      case "spacer":
        return { height: 50 };
      case "image":
        return { source: defaultImage, className: {} };
      case "label":
        return { name: "label", className: {}, inline_style: {}, icon: "" };
      case "form":
        return { name: "Form", config: getDefaultConfig("button") };
      case "tabview":
        const newId = generateObjectId();
        const newId2 = generateObjectId();

        const newObj = {
          display: "Tab 1",
          id: newId,
          icon: ""
        };
        const newObj2 = {
          display: "Tab 2",
          id: newId2,
          icon: ""
        };

        nestedGridData.current[newId] = [];
        nestedGridData.current[newId2] = [];

        return {
          display: "Tabview",
          config: {
            headerData: [newObj, newObj2],
            bodyData: {
              [newId]: [],
              [newId2]: []
            }
          },
          vertical: false,
          className: {},
          inline_style: {},
          icon: ""
        };
      case "recordsview":
      case "chart":
      case "detailsview":
      case "pivotcharts":
        const obj = Object.values(layoutData.current).find((item) => item.id === id);
        if (obj) {
          toastError("Block Already Exists");
          return null;
        }
        const blockData = getAppBlockData(id);
        return blockData;
      default:
        return null;
    }
  };

  const onDrop = (elemParams, item, e) => {
    if (!item || !item.i) {
      return null;
    }
    let element = e.dataTransfer.getData("type");
    if (!element || element === "null") return null;
    setDragFromOutside(false);
    let elementObject = JSON.parse(element);
    const { type } = elementObject;
    let elementConfig = getElementConfig(elementObject);
    if (!elementConfig) return null;
    if (screenBlocks.includes(type)) {
      item.resizeHandles = ["se"];
      elementObject = { ...elementObject, ...elementConfig };
    } else {
      elementObject = { ...elementObject, ...elementConfig, id: item.i };
    }
    elementConfig = {
      ...elementObject,
      className: {},
      inline_style: {},
      icon: "",
      colorif: []
    };

    item.i = elementObject.id;
    layoutData.current[item.i] = elementObject;
    const newItem = getDropDimensionForScreen(item, elementObject.type, device);
    return newItem;
  };

  const openEditModal = (id) => {
    let config = {};
    const data = [];
    Object.values(layoutData.current).forEach((element) => {
      const elementType = element.type;
      if (!inlineElements.includes(elementType)) {
        data.push(element);
      }
    });
    config = {
      id: id,
      type: layoutData.current[id].type,
      show: true,
      device: currentScreenData.device,
      config: layoutData.current[id],
      elements: data
    };

    setEditElementRightsidebar(config);
  };

  const editScreenElement = (id) => {
    const type = layoutData.current[id].type;
    const isInlineEdit = inlineElements.includes(type);
    if (isInlineEdit) {
      openEditModal(id);
    } else {
      navigate(`../../${appId}/applicationblock/${id}`);
    }
  };

  const saveEditElement = (config) => {
    let newLayoutData = JSON.parse(JSON.stringify(layoutData.current));
    newLayoutData[config.id] = config;
    layoutData.current = newLayoutData;
    if (config.type === "html") {
      setEditElementRightsidebar({ show: false, id: "", config: {}, type: "" });
    } else {
      setLayouts([...layouts]);
    }
  };
  const closeEditRightsidebar = () => {
    setEditElementRightsidebar({
      id: "",
      type: "",
      show: false,
      config: {}
    });
  };
  const typeBasedSize = (type) => {
    switch (type) {
      case "button-group":
      case "html":
      case "dynamic-html":
        return "medium";
      default:
        return "small";
    }
  };
  return (
    <div className="droppableContainer p_3" style={{ marginBottom: layouts.length >= 1 ? "400px" : "0px" }}>
      <DropLayout
        layout={layouts}
        setLayout={setLayouts}
        layoutData={layoutData}
        nestedGridData={nestedGridData}
        dragFromOutside={dragFromOutside}
        setDragFromOutside={setDragFromOutside}
        onDrop={onDrop}
        editElement={editScreenElement}
      />
      <BuilderRightSideBar
        isOpen={editElementRightsidebar.show}
        onClose={closeEditRightsidebar}
        heading={editElementRightsidebar.type}
        size={typeBasedSize(editElementRightsidebar.type)}
      >
        <EditScreenField
          editElementRightsidebar={editElementRightsidebar}
          saveElement={saveEditElement}
          device={device}
        />
      </BuilderRightSideBar>
    </div>
  );
};

export default ScreenGridLayout;
